import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { LoadingSpinner } from '../common/Badges';
import { useLang } from '../../i18n';

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className="card p-5 flex items-center gap-4" data-testid={testId}>
    <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  </div>
);

const AdminEmailAnalytics = () => {
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { setStats(await api('/api/admin/email-stats')); }
      catch { toast.error('Failed to load email stats'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!stats) return null;

  const maxDay = Math.max(1, ...stats.by_day.map(d => d.sent + d.failed));

  return (
    <div data-testid="admin-email-analytics" className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
        <Mail className="w-8 h-8 text-red-500" /> {t('admin_emails')}
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={BarChart3} label={t('emails_total')} value={stats.total} color="bg-gray-700" testId="email-stat-total" />
        <StatCard icon={CheckCircle} label={t('emails_sent')} value={stats.sent} color="bg-green-700" testId="email-stat-sent" />
        <StatCard icon={XCircle} label={t('emails_failed')} value={stats.failed} color="bg-red-700" testId="email-stat-failed" />
      </div>

      <div className="card p-5">
        <h2 className="text-lg font-bold text-white mb-4">{t('emails_last30')}</h2>
        {stats.by_day.length === 0 ? (
          <p className="text-gray-500 text-sm">{t('no_emails')}</p>
        ) : (
          <div className="flex items-end gap-1 h-32" data-testid="email-daily-chart">
            {stats.by_day.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col justify-end items-center gap-0.5" title={`${d.date}: ${d.sent} sent, ${d.failed} failed`}>
                {d.failed > 0 && <div className="w-full bg-red-600 rounded-t" style={{ height: `${(d.failed / maxDay) * 100}%`, minHeight: 3 }} />}
                {d.sent > 0 && <div className="w-full bg-green-600 rounded-t" style={{ height: `${(d.sent / maxDay) * 100}%`, minHeight: 3 }} />}
                <span className="text-[9px] text-gray-500 rotate-0 truncate w-full text-center">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        <h2 className="text-lg font-bold text-white p-5 pb-0">{t('recent_emails')}</h2>
        <div className="overflow-x-auto mt-3">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('email_to').toUpperCase()}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('email_subject').toUpperCase()}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('email_status').toUpperCase()}</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-400 tracking-wider">{t('email_date').toUpperCase()}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {stats.recent.map((e, i) => (
                <tr key={i} className="hover:bg-gray-800" data-testid={`email-log-row-${i}`}>
                  <td className="px-4 py-3 text-sm text-gray-300">{e.to}</td>
                  <td className="px-4 py-3 text-sm text-gray-300 max-w-xs truncate">{e.subject}</td>
                  <td className="px-4 py-3">
                    {e.status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-900/40 text-green-400 text-xs font-semibold">✓ {t('emails_sent')}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-900/40 text-red-400 text-xs font-semibold" title={e.error || ''}>✕ {e.status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {stats.recent.length === 0 && <div className="p-8 text-center text-gray-400">{t('no_emails')}</div>}
      </div>
    </div>
  );
};

export default AdminEmailAnalytics;
